import type { Technology, TechnologyOption } from '../domain/technology';

type TechnologiesService = {
  sortTechnologies: (technologies: Technology[]) => Technology[];
  convertTechnologiesToOptions: (technologies: Technology[]) => TechnologyOption[];
  removeDuplicateTechnologies: (technologies: Technology[]) => Technology[];
};

const makeTechnologiesService = (): TechnologiesService => {
  const sortTechnologies = (technologies: Technology[]) => {
    return technologies.sort((a, b) => {
      if (a.category < b.category) {
        return -1;
      }
      if (a.category > b.category) {
        return 1;
      }

      if (a.subCategory < b.subCategory) {
        return -1;
      }
      if (a.subCategory > b.subCategory) {
        return 1;
      }

      if (a.technologyName < b.technologyName) {
        return -1;
      }
      if (a.technologyName > b.technologyName) {
        return 1;
      }

      return 0;
    });
  };

  const removeDuplicateTechnologies = (technologies: Technology[]): Technology[] => {
    const uniqueIds = new Set<number>();

    return technologies.filter((tech) => {
      if (!uniqueIds.has(tech.technologyId)) {
        uniqueIds.add(tech.technologyId);
        return true;
      }
      return false;
    });
  };

  const createOption = (category: string, subCategory?: string) => ({
    name: subCategory || category,
    isChild: !!subCategory,
    items: [],
    count: 0,
    parent: category,
  });

  const convertTechnologiesToOptions = (technologies: Technology[]): TechnologyOption[] => {
    const optionsMap = technologies.reduce((map, tech) => {
      const { category, subCategory } = tech;
      const categoryKey = `${category.toLowerCase()}_parent`;
      const subCategoryKey = `${subCategory.toLowerCase()}_child`;

      if (!map.has(categoryKey)) {
        map.set(categoryKey, createOption(category));
      }
      (map.get(categoryKey) as TechnologyOption).count++;

      if (!map.has(subCategoryKey)) {
        map.set(subCategoryKey, createOption(category, subCategory));
      }
      map.get(subCategoryKey)!.count++;
      map.get(categoryKey)!.items.push(subCategory);

      return map;
    }, new Map<string, TechnologyOption>());

    const optionsArray = Array.from(optionsMap.values()).map((option) => ({
      ...option,
      items: [...new Set(option.items)],
    }));

    return optionsArray;
  };

  return { sortTechnologies, removeDuplicateTechnologies, convertTechnologiesToOptions };
};

export const TechnologiesService = makeTechnologiesService();
